import classNames from 'classnames';
import { ButtonHTMLAttributes, PropsWithChildren } from 'react';
import Spinner from './Spinner';

interface ButtonProps extends PropsWithChildren, ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  spinner?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function Button({
  children,
  variant = 'primary',
  size = 'md',
  className,
  spinner = false,
  onClick,
  ...rest
}: ButtonProps) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        'relative inline-flex flex-row items-center justify-center rounded-md border-2 p-3 px-5 text-center font-bold outline-offset-1 outline-none focus-visible:border-black focus-visible:outline-4 focus-visible:outline-blue-200 focus-visible:outline-solid',
        variant === 'primary' &&
          'border-green-600 bg-green-600 text-white hover:border-green-800 hover:bg-green-800 active:bg-green-800 disabled:border-green-600/0 disabled:bg-green-600/25',
        variant === 'secondary' &&
          'border-neutral-500 bg-neutral-500 text-white hover:border-neutral-800 hover:bg-neutral-800 focus:bg-neutral-800 disabled:border-neutral-800/0 disabled:bg-neutral-800/25',
        variant === 'tertiary' &&
          'border-purple-800 bg-purple-800 text-white hover:border-purple-950 hover:bg-purple-950 focus:bg-purple-950 disabled:border-purple-950/0 disabled:bg-purple-950/25',
        variant === 'danger' &&
          'border-red-600 bg-white text-red-600 hover:bg-red-600 hover:text-white focus:bg-red-600 focus:text-white focus:outline-red-100 disabled:border-red-600/25 disabled:bg-white disabled:text-red-600/25',
        variant === 'ghost' &&
          'disabled:text-secondary/25 border-transparent bg-transparent text-blue-700 hover:bg-neutral-100 hover:text-blue-900 focus-visible:bg-neutral-100 focus-visible:text-blue-900 disabled:bg-transparent',
        size === 'md' && 'min-h-[52px] min-w-20',
        size === 'sm' && 'text-md min-h-[36px] !px-4 py-1 font-normal',
        size === 'lg' && 'min-h-[80px] !p-6 !px-10 text-xl',
        className
      )}
      role={rest.role ?? 'button'}
      {...rest}
    >
      <div
        className={classNames(
          spinner && 'invisible',
          'inline-flex flex-row items-center justify-center'
        )}
      >
        {children}
      </div>
      {spinner && (
        <div className="absolute top-0 left-0 flex h-full w-full items-center justify-center">
          <Spinner size="sm" />
        </div>
      )}
    </button>
  );
}

export default Button;
